<template>
  <Modal
    :model-value="modelValue"
    content-class="!max-w-3xl w-full"
    :show-close="true"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="flex flex-col gap-4">
      <h2 class="text-xl font-semibold">{{ t('title') }}</h2>

      <div
        v-if="entry"
        class="rounded-md border border-obsidian-100 bg-obsidian-50 p-3 font-mono text-xs leading-snug text-obsidian-800"
      >
        <div class="flex justify-between gap-3">
          <span
            >{{ formatDate(entry.bookedDate, DateTime.DATE_SHORT) }} ·
            {{ entry.counterpartyName || '—' }}</span
          >
          <span class="font-semibold tabular-nums">{{ formatCurrency(entry.amount) }}</span>
        </div>
        <p class="mt-1 whitespace-pre-wrap">{{ entry.remittance || entry.endToEndId || '—' }}</p>
        <p class="mt-1 text-obsidian-500">
          {{ entry.accountIban }}
          <span v-if="entry.counterpartyIban"> · {{ entry.counterpartyIban }}</span>
        </p>
      </div>

      <Input
        :model-value="search"
        :placeholder="t('searchPlaceholder')"
        variant="ghost"
        @update:model-value="emit('update:search', String($event ?? ''))"
      />

      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner class="text-hazel-600" />
      </div>

      <EntityListExceptions
        v-else
        :status="status"
        :error="error"
        :items-count="items.length"
        :no-items-text="search ? t('noSearchResults') : t('noSuggestions')"
        small
      />

      <div v-if="items.length" class="max-h-96 space-y-3 overflow-y-auto p-1.5">
        <div
          v-for="item in displayItems"
          :key="item.key"
          data-testid="bank-statement-candidate"
          class="rounded-lg border border-obsidian-400 px-4 py-4 text-sm"
          :class="
            selectedKey === item.key ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : ''
          "
        >
          <div
            class="flex w-full cursor-pointer items-start justify-between gap-3 text-left"
            @click="handleSelectCard(item)"
          >
            <div class="min-w-0">
              <p class="font-medium text-obsidian-900">
                <router-link
                  v-if="item.booking"
                  :to="{ name: 'bookingEdit', params: { id: item.booking.id } }"
                  class="text-link"
                  :class="getHighlightClass(item.highlights?.bookingNumber)"
                  @click.stop
                >
                  {{ t('bookingLabel', { number: item.booking.bookingNumber }) }}
                </router-link>
                <span v-else :class="getHighlightClass(item.highlights?.bookingNumber)">
                  {{ t(item.type) }}
                </span>
                <router-link
                  v-if="item.invoice?.completeInvoiceNumber"
                  :to="{ name: 'invoice', params: { id: item.invoice.id } }"
                  class="text-link ml-2 font-normal"
                  :class="getHighlightClass(item.highlights?.invoiceNumber)"
                  @click.stop
                >
                  {{ item.invoice.completeInvoiceNumber }}
                </router-link>
              </p>
              <p class="mt-1 flex flex-wrap items-center gap-2 text-obsidian-600">
                <span :class="getHighlightClass(item.highlights?.name)">
                  {{ item.counterpartyName || t('noName') }}
                </span>
                <CounterpartyTypeBadge
                  v-if="item.counterpartyType"
                  :counterparty-type="item.counterpartyType as any"
                  size="sm"
                />
                <BookingStatusBadge v-if="item.booking" :status="item.booking.status" size="sm" />
                <Badge
                  v-if="item.highlights?.knownIban"
                  variant="success"
                  size="sm"
                  :icon="LandmarkIcon"
                  >{{ t('knownIban') }}</Badge
                >
              </p>
            </div>
            <div class="text-right">
              <p v-if="item.groupedPayments" class="text-sm text-obsidian-500">
                {{ t('paymentCount', { count: item.existingPayments?.length ?? 0 }) }}
              </p>
              <p
                v-else
                class="font-semibold tabular-nums"
                :class="getHighlightClass(item.highlights?.amount)"
              >
                {{ formatCurrency(item.amount) }}
              </p>
              <div
                v-if="selectedKey === item.key || !canChoosePayment(item)"
                class="mt-1 flex justify-end"
              >
                <BankStatementActionBadge :action="getItemAction(item)" />
              </div>
            </div>
          </div>

          <div
            v-if="selectedKey === item.key && canChoosePayment(item)"
            class="mt-3 space-y-2 border-t border-obsidian-100 pt-3"
          >
            <label class="flex cursor-pointer items-center gap-3">
              <input
                type="radio"
                :name="`bank-statement-payment-${item.key}`"
                :class="radiobutton({ size: 'md' })"
                :checked="isOptionSelected(item, null)"
                @change="handleSelectItem(item, null)"
              />
              <span>{{ t('createNewPayment') }}</span>
            </label>
            <label
              v-for="option in getLinkChoices(item)"
              :key="option.id"
              class="flex cursor-pointer items-center gap-3"
            >
              <input
                type="radio"
                :name="`bank-statement-payment-${item.key}`"
                :class="radiobutton({ size: 'md' })"
                :checked="isOptionSelected(item, option.id)"
                @change="handleSelectItem(item, option.id)"
              />
              <span class="min-w-0 flex-1 truncate">
                {{
                  option.paidDate || option.date
                    ? t('existingTransfer', {
                        date: formatDate(option.paidDate ?? option.date, DateTime.DATE_SHORT),
                      })
                    : t('existingTransferUndated')
                }}
                <span v-if="option.referenceId" class="text-obsidian-500">
                  · {{ option.referenceId }}</span
                >
              </span>
              <span class="tabular-nums">{{ formatCurrency(option.amount) }}</span>
            </label>
            <p v-if="!getLinkChoices(item).length" class="text-xs text-obsidian-500">
              {{ t('noExistingTransfers') }}
            </p>
          </div>

          <BankStatementPayerOpenAmounts
            v-if="selectedKey === item.key && canAddPayerOpenAmounts"
            v-model:selected-keys="extraKeys"
            v-model:open="payerListOpen"
            class="mt-3"
            :entry-amount="entry?.amount ?? 0"
            :payer-name="item.counterpartyName"
            :items="payerItems"
            :anchor="selected as BankStatementOpenAmountDto"
            :is-loading="payerQuery.isFetching.value"
          />
        </div>
      </div>

      <div class="flex justify-end gap-3 border-t border-obsidian-100 pt-4">
        <CVButton variant="secondary" @click="emit('update:modelValue', false)">{{
          t('cancel')
        }}</CVButton>
        <CVButton
          variant="default"
          :disabled="!selected && !extraKeys.length"
          :is-loading="isConfirming"
          @click="handleSelect"
        >
          {{ confirmLabel }}
        </CVButton>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/components/Modal.vue';
import Input from '@/components/Input.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import CounterpartyTypeBadge from '@/components/badges/CounterpartyTypeBadge.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { formatDate } from '@/hooks/use-formatted-date';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { radiobutton } from '@carvia/theme/lib/cva/radiobutton';
import BankStatementActionBadge from './BankStatementActionBadge.vue';
import BankStatementPayerOpenAmounts from './BankStatementPayerOpenAmounts.vue';
import Badge from '@/components/Badge.vue';
import BookingStatusBadge from '@/components/badges/BookingStatusBadge.vue';
import { LandmarkIcon } from 'lucide-vue-next';
import { doOpenAmountsOverlap, getActionForOpenAmountType } from '@/api/bank-statements.service';
import { useBankStatementPayerOpenAmounts } from '@/queries/use-bank-statements';
import {
  BankStatementAction,
  BankStatementOpenAmountType,
  type BankStatementEntryDto,
  type BankStatementOpenAmountDto,
  type Payment,
} from '@carvia/ros-client-types';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    search: string;
    entry: BankStatementEntryDto | null;
    items: BankStatementOpenAmountDto[];
    isLoading?: boolean;
    status?: 'pending' | 'error' | 'success';
    error?: unknown;
    isConfirming?: boolean;
  }>(),
  {
    isLoading: false,
    status: 'pending',
    error: undefined,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:search', value: string): void;
  /** `targets` holds more than one open amount for a collective payment, oldest due first. */
  (e: 'select', targets: BankStatementOpenAmountDto[], existingPaymentId: string | null): void;
}>();

const { t } = useI18n();
const selectedKey = ref<string | null>(null);
const selectedPaymentId = ref<string | null>(null);
const displayItems = computed(() => groupBookingPayments(props.items));
const selected = computed(
  () => displayItems.value.find((item) => item.key === selectedKey.value) ?? null,
);

const extraKeys = ref<string[]>([]);
const payerListOpen = ref(false);

watch(
  () => props.modelValue,
  () => {
    selectedKey.value = null;
    selectedPaymentId.value = null;
    extraKeys.value = [];
    payerListOpen.value = false;
  },
  { immediate: true },
);

// Only a new Payment can cover several open amounts; linking an existing one cannot.
const canAddPayerOpenAmounts = computed(
  () =>
    !!selected.value?.payerUserId &&
    !selectedPaymentId.value &&
    !selected.value.groupedPayments &&
    (selected.value.type === BankStatementOpenAmountType.INVOICE ||
      selected.value.type === BankStatementOpenAmountType.BOOKING),
);
const payerUserId = computed(() =>
  canAddPayerOpenAmounts.value ? (selected.value?.payerUserId ?? null) : null,
);
const payerQuery = useBankStatementPayerOpenAmounts(
  computed(() => props.entry?.id ?? null),
  payerUserId,
  computed(() => props.modelValue),
);
const payerItems = computed(() => {
  const anchor = canAddPayerOpenAmounts.value
    ? (selected.value as BankStatementOpenAmountDto)
    : null;
  return (payerQuery.data.value ?? []).filter(
    (item) => !anchor || !doOpenAmountsOverlap(anchor, item),
  );
});
const extraTargets = computed(() =>
  payerItems.value.filter((item) => extraKeys.value.includes(item.key)),
);
const confirmLabel = computed(() => {
  const count = extraTargets.value.length + (selected.value ? 1 : 0);
  if (count > 1) return t('confirmCollective', { count });
  if (selected.value) return t(`confirmAction.${getItemAction(selected.value)}`);
  return extraTargets.value.length ? t('confirmAction.CREATE_PAYMENT') : t('select');
});

watch(selectedKey, () => {
  extraKeys.value = [];
  payerListOpen.value = false;
});

const getHighlightClass = (on?: boolean) => (on ? 'bg-yellow-100 text-obsidian-900' : undefined);

// Payments synthesized from a PAYMENT open amount carry no creation date.
type PickerPayment = Payment;

type PickerItem = Omit<BankStatementOpenAmountDto, 'existingPayments'> & {
  existingPayments?: PickerPayment[];
  groupedPayments?: boolean;
};

function groupBookingPayments(items: BankStatementOpenAmountDto[]): PickerItem[] {
  const result: PickerItem[] = [];
  const indexByBooking = new Map<string, number>();
  for (const item of items) {
    if (item.type !== BankStatementOpenAmountType.PAYMENT || !item.booking) {
      result.push(item);
      continue;
    }
    const at = indexByBooking.get(item.booking.id);
    if (at == null) {
      indexByBooking.set(item.booking.id, result.length);
      result.push(item);
      continue;
    }
    const host = result[at];
    result[at] = {
      ...host,
      key: `BOOKING:${item.booking.id}:payments`,
      type: BankStatementOpenAmountType.BOOKING,
      id: item.booking.id,
      payment: null,
      groupedPayments: true,
      counterpartyType: host.counterpartyType ?? item.counterpartyType,
      existingPayments: mergeTransferPayments(host, item),
    };
  }
  return result;
}

function mergeTransferPayments(
  host: PickerItem,
  item: BankStatementOpenAmountDto,
): PickerPayment[] {
  const byId = new Map<string, PickerPayment>();
  const add = (payment?: PickerPayment) => {
    if (payment?.id) byId.set(payment.id, payment);
  };
  for (const payment of host.existingPayments ?? []) add(payment);
  for (const payment of item.existingPayments ?? []) add(payment);
  if (host.payment) add({ ...host.payment, amount: host.amount });
  if (item.payment) add({ ...item.payment, amount: item.amount });
  return [...byId.values()];
}

const canChoosePayment = (item: PickerItem) => {
  if (item.type === BankStatementOpenAmountType.SETTLEMENT) return false;
  if (item.type === BankStatementOpenAmountType.PAYMENT) return !!(item.booking || item.invoice);
  return (
    item.type === BankStatementOpenAmountType.INVOICE ||
    item.type === BankStatementOpenAmountType.BOOKING
  );
};

const getLinkChoices = (item: PickerItem): PickerPayment[] => {
  const payments: PickerPayment[] = [...(item.existingPayments ?? [])];
  if (
    item.type === BankStatementOpenAmountType.PAYMENT &&
    item.payment &&
    !payments.some((payment) => payment.id === item.payment?.id)
  ) {
    payments.unshift({ ...item.payment, amount: item.amount });
  }
  return payments;
};

const isOptionSelected = (item: PickerItem, paymentId: string | null) =>
  selectedKey.value === item.key && selectedPaymentId.value === paymentId;

const getItemAction = (item: PickerItem): BankStatementAction => {
  if (selectedKey.value !== item.key) return getActionForOpenAmountType(item.type);
  if (selectedPaymentId.value) return BankStatementAction.LINK_PAYMENT;
  if (canChoosePayment(item)) return BankStatementAction.CREATE_PAYMENT;
  return getActionForOpenAmountType(item.type);
};

const handleSelectCard = (item: PickerItem) => {
  handleSelectItem(
    item,
    item.type === BankStatementOpenAmountType.PAYMENT ? (item.payment?.id ?? null) : null,
  );
};

const handleSelectItem = (item: PickerItem, paymentId: string | null) => {
  selectedKey.value = item.key;
  selectedPaymentId.value = paymentId;
  if (paymentId) extraKeys.value = [];
};

const handleSelect = () => {
  const anchor = selected.value as BankStatementOpenAmountDto | null;
  const targets = anchor ? [anchor, ...extraTargets.value] : extraTargets.value;
  if (!targets.length) return;
  emit('select', targets, selectedPaymentId.value);
};
</script>

<i18n lang="json">
{
  "en": {
    "knownIban": "Known IBAN",
    "confirmCollective": "Create payment for {count} items",
    "title": "Choose target",
    "searchPlaceholder": "Search booking, invoice, name…",
    "noSuggestions": "No matching open amounts",
    "noSearchResults": "No open amounts found",
    "bookingLabel": "Booking {number}",
    "INVOICE": "Invoice",
    "BOOKING": "Booking",
    "PAYMENT": "Payment",
    "SETTLEMENT": "Merchant payout",
    "noName": "No name",
    "paymentCount": "{count} payments",
    "cancel": "Cancel",
    "select": "Select",
    "createNewPayment": "Create new payment",
    "existingTransfer": "Existing transfer from {date}",
    "existingTransferUndated": "Existing transfer",
    "noExistingTransfers": "No unlinked transfers on this target.",
    "confirmAction": {
      "CREATE_PAYMENT": "Create payment",
      "LINK_PAYMENT": "Link",
      "LINK_SETTLEMENT": "Link"
    }
  },
  "de": {
    "knownIban": "Bekannte IBAN",
    "confirmCollective": "Zahlung für {count} Posten anlegen",
    "title": "Ziel wählen",
    "searchPlaceholder": "Buchung, Rechnung, Name suchen…",
    "noSuggestions": "Keine passenden offenen Beträge",
    "noSearchResults": "Keine offenen Beträge gefunden",
    "bookingLabel": "Buchung {number}",
    "INVOICE": "Rechnung",
    "BOOKING": "Buchung",
    "PAYMENT": "Zahlung",
    "SETTLEMENT": "Händlerauszahlung",
    "noName": "Kein Name",
    "paymentCount": "{count} Zahlungen",
    "cancel": "Abbrechen",
    "select": "Auswählen",
    "createNewPayment": "Neue Zahlung anlegen",
    "existingTransfer": "Bestehende Überweisung vom {date}",
    "existingTransferUndated": "Bestehende Überweisung",
    "noExistingTransfers": "Keine unverknüpfte Überweisung zu diesem Ziel.",
    "confirmAction": {
      "CREATE_PAYMENT": "Zahlung anlegen",
      "LINK_PAYMENT": "Verknüpfen",
      "LINK_SETTLEMENT": "Verknüpfen"
    }
  },
  "es": {
    "knownIban": "IBAN conocido",
    "confirmCollective": "Crear pago para {count} partidas",
    "title": "Elegir destino",
    "searchPlaceholder": "Buscar reserva, factura, nombre…",
    "noSuggestions": "No hay importes abiertos coincidentes",
    "noSearchResults": "No se encontraron importes abiertos",
    "bookingLabel": "Reserva {number}",
    "INVOICE": "Factura",
    "BOOKING": "Reserva",
    "PAYMENT": "Pago",
    "SETTLEMENT": "Pago al comercio",
    "noName": "Sin nombre",
    "paymentCount": "{count} pagos",
    "cancel": "Cancelar",
    "select": "Seleccionar",
    "createNewPayment": "Crear pago nuevo",
    "existingTransfer": "Transferencia existente del {date}",
    "existingTransferUndated": "Transferencia existente",
    "noExistingTransfers": "No hay transferencias sin vincular para este destino.",
    "confirmAction": {
      "CREATE_PAYMENT": "Crear pago",
      "LINK_PAYMENT": "Vincular",
      "LINK_SETTLEMENT": "Vincular"
    }
  }
}
</i18n>
